import { PlusIcon } from 'components/icons';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { TabsContent } from 'components/ui/tabs';
import { useState } from 'react';
import { useGetHomeWidgetsQuery } from 'store/api/home';
import { useGetWidgetGalleryQuery } from 'store/api/homeWidgetSharing';
import { useTourMode } from 'tour/tourMode';
import { FeatureFlag } from 'types/features';
import {
    HOME_BUILT_IN_SIZES,
    type HomeBuiltIn,
    type HomeEntry,
    type HomePlacement,
    type HomeWidgetSize,
} from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { getIntl, t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { FORM_LABEL, formIcon, periodLabel } from '../creator/catalogueLabels';
import { WidgetCreator } from '../creator/WidgetCreator';
import { addsGap, BUILT_IN_TITLE, isOnHome, SIZE_NAME } from '../grid/layout';
import { WidgetMenu } from '../WidgetMenu';
import { AddToHome } from './AddToHome';
import { FirmGalleryTab } from './FirmGalleryTab';

/** The built-in sections in the order a new Home has them, each with the line that says what it holds. */
const BUILT_INS: { builtIn: HomeBuiltIn; aboutId: string }[] = [
    { builtIn: 'MY_CASES', aboutId: 'home.widgets.about.my-cases' },
    { builtIn: 'SAVED_FOR_LATER', aboutId: 'home.widgets.about.saved-for-later' },
    { builtIn: 'MY_TIME', aboutId: 'home.widgets.about.my-time' },
    { builtIn: 'MY_TIME_RECORDS', aboutId: 'home.widgets.about.my-time-records' },
    { builtIn: 'CONTINUE', aboutId: 'home.widgets.about.continue' },
    { builtIn: 'TO_REVIEW', aboutId: 'home.widgets.about.to-review' },
];

/** The dialog's tabs: what is the person's own, and what the firm published. */
export type AddWidgetTab = 'yours' | 'gallery';

type Props = {
    open: boolean;
    onClose: () => void;
    /** The tab it shows until the person picks one. */
    tab?: AddWidgetTab;
    /** The layout being arranged, which an added widget joins. */
    entries: HomeEntry[];
    /** How many entries that layout may hold. */
    maxOwnEntries: number;
    /** `size` is the one chosen for a widget just built; anything else lands at its smallest. */
    onAdd: (placement: HomePlacement, size?: HomeWidgetSize) => void;
};

/**
 * What can be put on Home, opened while arranging. Each tab lists cards that end in {@link AddToHome}; the footer
 * says where an added widget lands or, on the firm's gallery, whose numbers and whose changes it shows, and on
 * either tab that Home is full. New widget opens the creator over it, and a widget built there joins the layout
 * being arranged.
 *
 * A guided tour opens it to show it: it then takes no input, and it is not modal, which would hide the tour's card
 * from assistive technology and keep the focus off it. Nor does it move into place: the tour rings a tab in it where
 * that tab first stands.
 */
export function AddWidgetDialog({ open, onClose, ...body }: Props) {
    const touring = useTourMode();
    return (
        <Dialog
            open={open}
            modal={!touring}
            disablePointerDismissal={touring}
            onOpenChange={(next) => !next && onClose()}
        >
            <DialogContent
                inert={touring}
                className={cn(
                    'flex h-[760px] max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden rounded-[14px] p-0 sm:max-w-[1040px]',
                    touring && 'animate-none! transition-none!'
                )}
            >
                <AddWidgetBody onClose={onClose} {...body} />
            </DialogContent>
        </Dialog>
    );
}

/** The dialog's content, mounted only while it is open: what a tab reads, it reads then. */
function AddWidgetBody({ onClose, tab = 'yours', entries, maxOwnEntries, onAdd }: Omit<Props, 'open'>) {
    const full = entries.length >= maxOwnEntries;
    const { data: built = [] } = useGetHomeWidgetsQuery();
    // Read here for the count on the tab; the tab itself reads the same answer.
    const { data: gallery } = useGetWidgetGalleryQuery();
    const [picked, setPicked] = useState<AddWidgetTab>();
    const active = picked ?? tab;
    const [isCreating, setIsCreating] = useState(false);
    const offersDrafts = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);

    return (
        <>
            {/* The right padding keeps the row clear of the dialog's own close button, which the kit pins to the right
                edge whatever the direction: physical, as that is. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-[18px] pr-14 pl-6">
                {/* The basis is what lets the row wrap: at `flex-1` the title would shrink to a word a line instead. */}
                <DialogTitle className="text-dialog-title flex-[1_1_8rem] font-semibold">
                    {t('home.widgets.add-dialog.title')}
                </DialogTitle>
                {/* A full Home has no place for a new widget, as the footer says. */}
                {full ? null : (
                    <Button
                        variant="secondary"
                        className="ps-3 pe-3.5 font-semibold"
                        onClick={() => setIsCreating(true)}
                    >
                        <PlusIcon />
                        {t('home.widgets.add-dialog.new')}
                    </Button>
                )}
            </div>
            <LineTabs
                value={active}
                onValueChange={(next: AddWidgetTab) => setPicked(next)}
                className="flex min-h-0 flex-1 flex-col"
            >
                <LineTabsList className="border-neutral-tint px-6 pt-2">
                    <LineTab value="yours">{t('home.widgets.add-dialog.tab.yours')}</LineTab>
                    <LineTab
                        value="gallery"
                        data-tour="add-widget-gallery"
                        className="gap-1.5"
                        icon={
                            gallery?.length ? (
                                <span className="bg-primary-tint text-primary-ink text-caption flex h-[18px] items-center rounded-full px-1.5 font-semibold">
                                    {gallery.length}
                                </span>
                            ) : null
                        }
                    >
                        {t('home.widgets.add-dialog.tab.gallery')}
                    </LineTab>
                </LineTabsList>
                <TabsContent value="yours" className="flex min-h-0 flex-col gap-6 overflow-y-auto px-6 pt-5 pb-6">
                    <section className="flex flex-col gap-2.5">
                        <h3 className="text-label text-muted-foreground uppercase">
                            {t('home.widgets.add-dialog.built-in')}
                        </h3>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                            {BUILT_INS.filter(({ builtIn }) => offersDrafts || builtIn !== 'SAVED_FOR_LATER').map(
                                ({ builtIn, aboutId }) => {
                                    const placement: HomePlacement = { kind: 'BUILT_IN', builtIn };
                                    return (
                                        <article
                                            key={builtIn}
                                            className="border-neutral-tint bg-surface-tile flex flex-col gap-1.5 rounded-md border p-3.5"
                                        >
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-body1 min-w-0 flex-1 font-semibold">
                                                    {t(BUILT_IN_TITLE[builtIn])}
                                                </h4>
                                                <span className="text-muted-foreground text-caption whitespace-nowrap">
                                                    {getIntl().formatList(
                                                        HOME_BUILT_IN_SIZES[builtIn].map((size) => t(SIZE_NAME[size])),
                                                        { type: 'unit' }
                                                    )}
                                                </span>
                                            </div>
                                            <p className="text-foreground-secondary text-meta flex-1">{t(aboutId)}</p>
                                            <AddToHome
                                                onHome={isOnHome(entries, placement)}
                                                full={full}
                                                entries={entries}
                                                placement={placement}
                                                onAdd={() => onAdd(placement)}
                                            />
                                        </article>
                                    );
                                }
                            )}
                        </div>
                    </section>
                    {built.length > 0 ? (
                        <section className="flex flex-col gap-2.5">
                            <h3 className="text-label text-muted-foreground uppercase">
                                {t('home.widgets.add-dialog.built')}
                            </h3>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                                {built.map((widget) => {
                                    const placement: HomePlacement = { kind: 'WIDGET', widget };
                                    const onHome = isOnHome(entries, placement);
                                    const Icon = formIcon(widget.definition);
                                    return (
                                        <article
                                            key={widget.id}
                                            className="border-neutral-tint bg-surface-tile flex flex-col gap-1.5 rounded-md border p-3.5"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                <span className="bg-primary-tint text-primary-ink flex size-8 flex-none items-center justify-center rounded-md">
                                                    <Icon className="size-4" />
                                                </span>
                                                <div className="flex min-w-0 flex-1 flex-col">
                                                    <h4 className="text-body1 truncate leading-[1.43] font-semibold">
                                                        {widget.name}
                                                    </h4>
                                                    <span className="text-muted-foreground text-caption">
                                                        {t(FORM_LABEL[widget.definition.form])} ·{' '}
                                                        {periodLabel(widget.definition.period)}
                                                    </span>
                                                </div>
                                                {/* A widget on Home is changed from its own card, once Home is saved:
                                                    the layout being arranged holds it as it was. */}
                                                {onHome ? null : <WidgetMenu widget={widget} />}
                                            </div>
                                            <AddToHome
                                                onHome={onHome}
                                                full={full}
                                                entries={entries}
                                                placement={placement}
                                                onAdd={() => onAdd(placement)}
                                            />
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    ) : null}
                </TabsContent>
                <TabsContent value="gallery" className="flex min-h-0 flex-col">
                    <FirmGalleryTab entries={entries} full={full} onAdd={onAdd} />
                </TabsContent>
            </LineTabs>
            <div className="border-neutral-tint flex items-center gap-3 border-t px-6 py-3.5 max-sm:flex-col max-sm:items-stretch">
                <p className="text-muted-foreground text-meta flex-1">
                    {full
                        ? t('home.widgets.add-dialog.full', { max: String(maxOwnEntries) })
                        : t(active === 'gallery' ? 'home.widgets.gallery.note' : 'home.widgets.add-dialog.lands')}
                </p>
                <Button variant="outline" className="border-border-control px-4" onClick={onClose}>
                    {t('home.widgets.add-dialog.back')}
                </Button>
            </div>
            <WidgetCreator
                open={isCreating}
                onClose={() => setIsCreating(false)}
                onCreated={(widget, size) => {
                    onAdd({ kind: 'WIDGET', widget }, size);
                    onClose();
                }}
                leavesGap={(size) => addsGap(entries, [...entries, { size }])}
            />
        </>
    );
}
